import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import * as React from 'react';

import { CodeData } from '@/components/Code/Code';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';

import { UntypedInput } from '../../../control/state';
import { Message } from '../../../data/message';
import { isValidSchema, resolveRef } from '../../../json-schema-utils';
import { isSchemaMessageArray, isSchemaMessageType } from '../../../utils';

type SchemaState = Record<string, unknown>;

type SchemaEditorOptions = {
  // Make a best-effort attempt to expand the schema if there are few properties but contract if there are a lot
  smartExpand?: boolean;

  hideRequiredLabels?: boolean;
  disableAutoFocus?: boolean;
  hideDeleteKey?: boolean;
  showNode?: boolean;
};

const InternalSchemaEditor = (props: {
  input: UntypedInput;
  onChange: (state: SchemaState, touched: string[] | undefined) => void;
  readOnly?: boolean;
  renderer: SchemaRenderer;
  schema: JSONSchema7Definition | null | undefined;
  rootSchema: JSONSchema7 | null | undefined;
  skipEmpty?: boolean;
  options?: SchemaEditorOptions;
}) => {
  const [touched, setTouched] = React.useState<string[]>([]);

  const { onChange, input } = props;
  const updateForKey = React.useCallback(
    (keyPath: string[] | undefined, value: unknown) => {
      if (keyPath != null) {
        const nextState = { ...input };
        let current = nextState;

        for (let i = 0; i < keyPath.length - 1; i++) {
          const currentValue = current[keyPath[i]];
          current[keyPath[i]] =
            typeof currentValue === 'object' && currentValue !== null
              ? { ...currentValue }
              : {};
          current = current[keyPath[i]] as Record<string, unknown>;
        }
        current[keyPath[keyPath.length - 1]] = value;

        const topLevelKey = keyPath[0];
        const nextTouched = [...new Set([...touched, topLevelKey])];

        onChange(nextState, nextTouched);
        setTouched(nextTouched);
      } else {
        onChange(value as SchemaState, undefined);
      }
    },
    [onChange, input, touched]
  );

  const deleteForKey = React.useCallback(
    (keyPath: string[] | undefined) => {
      if (keyPath != null) {
        const nextState = { ...input };
        let current = nextState;

        // Navigate to parent object
        for (let i = 0; i < keyPath.length - 1; i++) {
          const currentValue = current[keyPath[i]];
          if (typeof currentValue !== 'object' || currentValue === null) {
            return; // Can't navigate further, exit
          }
          current[keyPath[i]] = { ...currentValue };
          current = current[keyPath[i]] as Record<string, unknown>;
        }

        // Delete the key
        delete current[keyPath[keyPath.length - 1]];

        const topLevelKey = keyPath[0];
        const nextTouched = touched.filter((k) => k !== topLevelKey);

        onChange(nextState, nextTouched);
        setTouched(nextTouched);
      } else {
        onChange({} as SchemaState, undefined);
      }
    },
    [onChange, input, touched]
  );

  if (!isValidSchema(props.schema) || !isValidSchema(props.rootSchema)) {
    return (
      <div className="rounded-md border border-secondary">
        <CodeData
          value={props.input}
          onChange={(v) => props.onChange(v, Object.keys(v))}
        />
      </div>
    );
  }

  return renderSchemaBase({
    readOnly: props.readOnly ?? false,
    renderer: props.renderer,
    schema: props.schema,
    rootSchema: props.rootSchema,
    state: props.input,
    update: updateForKey,
    deleteKey: props.options?.hideDeleteKey ? undefined : deleteForKey,
    skipEmpty: props.skipEmpty ?? false,
    options: props.options ?? {},
  });
};

export const SchemaEditor = (props: {
  input: UntypedInput;
  onChange: (state: SchemaState, touched: string[] | undefined) => void;
  readOnly?: boolean;
  renderer: SchemaRenderer;
  schema: JSONSchema7Definition | null | undefined;
  rootSchema: JSONSchema7 | null | undefined;
  skipEmpty?: boolean;
  options?: SchemaEditorOptions;
}) => {
  return (
    <ErrorBoundary
      fallback={(error) => (
        <div className="rounded-md border border-secondary">
          <CodeData
            value={props.input}
            onChange={(v) => props.onChange(v, Object.keys(v))}
          />
          <ExpandableErrorAlert error={error} />
        </div>
      )}
    >
      <InternalSchemaEditor {...props} />
    </ErrorBoundary>
  );
};

export type SchemaRendererProps<ValueType, ChangeType = ValueType> = {
  readOnly?: boolean;
  required?: boolean;
  name: string;
  onChange: (value: ChangeType) => void;
  schema: JSONSchema7Definition;
  value: ValueType;
  options?: SchemaEditorOptions;
  openByDefault?: boolean;
  onDeleteKey?: () => void;
};

export type ForeignValue = {
  value: { ok: false } | { ok: true; value: unknown };
  rep: string;
};

export type SchemaRenderer = {
  StringArray: (props: SchemaRendererProps<string[]>) => JSX.Element | null;
  String: (props: SchemaRendererProps<string>) => JSX.Element | null;
  Number: (props: SchemaRendererProps<number>) => JSX.Element | null;
  NumberArray: (props: SchemaRendererProps<number[]>) => JSX.Element | null;
  Boolean: (props: SchemaRendererProps<boolean>) => JSX.Element | null;
  Message: (
    props: SchemaRendererProps<Message, Message | null>
  ) => JSX.Element | null;
  MessageArray: (
    props: SchemaRendererProps<Message[] | Message>
  ) => JSX.Element | null;
  MessageRecord: (
    props: SchemaRendererProps<Record<string, Message>>
  ) => JSX.Element | null;
  Default: (
    props: SchemaRendererProps<unknown> & {
      exists?: boolean;
    }
  ) => JSX.Element | null;
  Wrapper: (props: {
    value: unknown;
    schema: JSONSchema7Definition;
    children: React.ReactNode;
  }) => JSX.Element | null;
  Object: (
    props: Omit<SchemaRendererProps<Record<string, unknown>>, 'onChange'> & {
      children: React.ReactNode;
    }
  ) => JSX.Element | null;
};

const renderSchemaBase = ({
  readOnly,
  renderer,
  schema,
  state,
  rootSchema,
  update,
  deleteKey,
  skipEmpty = false,
  options,
}: {
  readOnly: boolean;
  renderer: SchemaRenderer;
  schema: JSONSchema7Definition;
  rootSchema: JSONSchema7;
  state: SchemaState;
  update: (key: string[] | undefined, value: unknown) => void;
  deleteKey: ((key: string[] | undefined) => void) | undefined;
  skipEmpty: boolean;
  options: SchemaEditorOptions;
}): React.ReactNode => {
  // ---- Unknown Edge Case ----

  if (typeof schema === 'boolean') {
    console.error('Unknown edge case');
    return <></>;
  }
  if (schema.type === 'object' && !isSchemaMessageType(schema)) {
    if (
      schema.additionalProperties &&
      typeof schema.additionalProperties !== 'boolean'
    ) {
      return (
        <renderer.Wrapper value={state} schema={schema}>
          {renderSchemaBase({
            readOnly,
            renderer,
            schema: schema.additionalProperties,
            rootSchema,
            state,
            update,
            deleteKey,
            skipEmpty,
            options,
          })}
        </renderer.Wrapper>
      );
    }

    if (schema.properties) {
      return Object.entries(schema.properties).length > 0 ? (
        <renderer.Wrapper value={state} schema={schema}>
          {Object.entries(schema.properties).map(([k, v]) => (
            <React.Fragment key={k}>
              {renderSchema({
                readOnly,
                parentKey: k,
                renderer,
                schema: v,
                rootSchema,
                state,
                update,
                deleteKey,
                skipEmpty,
                required: schema.required?.includes(k) ?? false,
                refDepth: 0,
                options,
                openFirstLevel:
                  (Object.entries(schema.properties!).length ?? 5) < 4,
                schemaDepth: 0,
              })}
            </React.Fragment>
          ))}
        </renderer.Wrapper>
      ) : (
        <div className="flex items-center px-1 py-2 text-disabled">
          No properties
        </div>
      );
    }
  }

  return renderSchema({
    readOnly,
    renderer,
    schema,
    rootSchema,
    state,
    update,
    deleteKey,
    skipEmpty,
    required: true,
    refDepth: 0,
    options,
    openFirstLevel: true,
    schemaDepth: 0,
  });
};

const renderSchema = ({
  readOnly,
  parentKey: _parentKey,
  renderer,
  schema,
  rootSchema,
  state,
  update,
  deleteKey,
  skipEmpty,
  required,
  refDepth,
  schemaDepth,
  options,
  openFirstLevel,
}: {
  readOnly: boolean;
  parentKey?: string | string[];
  renderer: SchemaRenderer;
  schema: JSONSchema7Definition;
  rootSchema: JSONSchema7;
  state: SchemaState;
  update: (key: string[] | undefined, value: unknown) => void;
  deleteKey: ((key: string[] | undefined) => void) | undefined;
  skipEmpty: boolean;
  required: boolean;
  refDepth: number;
  schemaDepth: number;
  options: SchemaEditorOptions;
  openFirstLevel: boolean;
}): React.ReactNode => {
  const parentKey: string[] | undefined = Array.isArray(_parentKey)
    ? _parentKey
    : _parentKey != null
    ? [_parentKey]
    : _parentKey;
  const name = parentKey?.at(-1) ?? 'State';
  const value = parentKey != null ? getNestedValue(state, parentKey) : state;
  // ---- Unknown Edge Case ----

  if (typeof schema === 'boolean') {
    console.error('Unknown edge case');
    return <></>;
  }

  if (
    skipEmpty &&
    parentKey != null &&
    getNestedValue(state, parentKey) === undefined
  ) {
    return <></>;
  }

  // ---- Message Types ----

  // Singular Message
  if (schema.$ref || isSchemaMessageType(schema)) {
    if (isSchemaMessageType(schema)) {
      return renderer.Message({
        readOnly,
        name,
        onChange: (v) => update(parentKey, v),
        onDeleteKey: deleteKey != null ? () => deleteKey(parentKey) : undefined,
        schema,
        value: (value ?? { type: 'user' }) as Message,
        required,
        options,
        openByDefault: openFirstLevel ? schemaDepth === 0 : false,
      });
    }

    const resolvedSchema = resolveRef(rootSchema, schema.$ref);
    if (resolvedSchema != null && refDepth < 5) {
      return renderSchema({
        readOnly,
        parentKey,
        renderer,
        schema: resolvedSchema,
        rootSchema,
        state,
        update,
        deleteKey,
        skipEmpty,
        required,
        refDepth: refDepth + 1,
        schemaDepth,
        options,
        openFirstLevel,
      });
    }
  }

  // Array of strings
  if (
    schema.type === 'array' &&
    typeof schema.items === 'object' &&
    'type' in schema.items &&
    schema.items.type === 'string'
  ) {
    return renderer.StringArray({
      readOnly,
      name,
      onChange: (v) => update(parentKey, v),
      onDeleteKey: deleteKey != null ? () => deleteKey(parentKey) : undefined,
      schema,
      value: value as string[],
      required,
      options,
      openByDefault: openFirstLevel ? schemaDepth === 0 : false,
    });
  }

  // Array of numbers
  if (
    schema.type === 'array' &&
    typeof schema.items === 'object' &&
    'type' in schema.items &&
    (schema.items.type === 'number' || schema.items.type === 'integer')
  ) {
    return renderer.NumberArray({
      readOnly,
      name,
      onChange: (v) => update(parentKey, v),
      onDeleteKey: deleteKey != null ? () => deleteKey(parentKey) : undefined,
      schema,
      value: value as number[],
      required,
      options,
      openByDefault: openFirstLevel ? schemaDepth === 0 : false,
    });
  }

  // Array of Messages
  if (isSchemaMessageArray(schema)) {
    return renderer.MessageArray({
      readOnly,
      name,
      onChange: (v) => update(parentKey, v),
      onDeleteKey: deleteKey != null ? () => deleteKey(parentKey) : undefined,
      schema,
      value: (value ?? []) as Message[],
      required,
      options,
      openByDefault: openFirstLevel ? schemaDepth === 0 : false,
    });
  }

  // Object of Messages
  if (
    schema.type === 'object' &&
    schema.additionalProperties !== undefined &&
    !Array.isArray(schema.additionalProperties) &&
    typeof schema.additionalProperties !== 'boolean' &&
    isSchemaMessageType(schema.additionalProperties)
  ) {
    return renderer.MessageRecord({
      readOnly,
      name,
      onChange: (v) => update(parentKey, v),
      onDeleteKey: deleteKey != null ? () => deleteKey(parentKey) : undefined,
      schema,
      value: (value ?? {}) as Record<string, Message>,
      required,
      options,
      openByDefault: openFirstLevel ? schemaDepth === 0 : false,
    });
  }

  // ---- Simple Primitives ----

  if (schema.type === 'string') {
    return renderer.String({
      readOnly,
      name,
      onChange: (v) => update(parentKey, v),
      onDeleteKey: deleteKey != null ? () => deleteKey(parentKey) : undefined,
      schema,
      value: value as string,
      required,
      options,
      openByDefault: openFirstLevel ? schemaDepth === 0 : false,
    });
  }

  if (schema.type === 'number' || schema.type === 'integer') {
    return renderer.Number({
      readOnly,
      name,
      onChange: (v) => update(parentKey, v),
      onDeleteKey: deleteKey != null ? () => deleteKey(parentKey) : undefined,
      schema,
      value: value as number,
      required,
      options,
      openByDefault: openFirstLevel ? schemaDepth === 0 : false,
    });
  }

  if (schema.type === 'boolean') {
    return renderer.Boolean({
      readOnly,
      name,
      onChange: (v) => update(parentKey, v),
      onDeleteKey: deleteKey != null ? () => deleteKey(parentKey) : undefined,
      schema,
      value: value as boolean,
      required,
      options,
      openByDefault: openFirstLevel ? schemaDepth === 0 : false,
    });
  }

  if (schema.anyOf) {
    const first = schema.anyOf[0];
    if (first)
      return renderSchema({
        readOnly,
        parentKey,
        renderer,
        schema: first,
        rootSchema,
        state,
        update,
        deleteKey,
        skipEmpty,
        required,
        refDepth,
        schemaDepth,
        options,
        openFirstLevel,
      });
  }

  if (schema.oneOf) {
    const first = schema.oneOf[0];
    if (first)
      return renderSchema({
        readOnly,
        parentKey,
        renderer,
        schema: first,
        rootSchema,
        state,
        update,
        deleteKey,
        skipEmpty,
        required,
        refDepth,
        options,
        openFirstLevel,
        schemaDepth,
      });
  }

  if (
    schema.type === 'object' &&
    schema.properties &&
    Object.keys(schema.properties).length > 0
  ) {
    return renderer.Object({
      readOnly,
      name,
      schema,
      onDeleteKey: deleteKey != null ? () => deleteKey(parentKey) : undefined,
      required,
      openByDefault: openFirstLevel ? schemaDepth === 0 : false,
      options,
      value: value as Record<string, unknown>,
      children: Object.entries(schema.properties).map(([key, propSchema]) => (
        <React.Fragment key={key}>
          {renderSchema({
            readOnly,
            parentKey: parentKey ? [...parentKey, key] : [key],
            renderer,
            schema: propSchema,
            rootSchema,
            state,
            update,
            deleteKey,
            skipEmpty,
            required: schema.required?.includes(key) ?? false,
            refDepth,
            options,
            schemaDepth: schemaDepth + 1,
            openFirstLevel,
          })}
        </React.Fragment>
      )),
    });
  }

  return renderer.Default({
    readOnly,
    name,
    onChange: (v) => update(parentKey, v),
    onDeleteKey: deleteKey != null ? () => deleteKey(parentKey) : undefined,
    schema,
    value: schema.type
      ? getDefaultForType(
          value,
          Array.isArray(schema.type) ? schema.type[0] : schema.type
        )
      : value,
    exists: value !== undefined,
    required,
    options,
    openByDefault: openFirstLevel ? schemaDepth === 0 : false,
  });
};

const getDefaultForType = (value: unknown, type: string) => {
  if (value !== undefined) {
    return value;
  }
  switch (type) {
    case 'string':
      return '';
    case 'number':
    case 'integer':
      return 0;
    case 'boolean':
      return false;
    case 'object':
      return {};
    case 'array':
      return [];
    default:
      return undefined;
  }
};

// Helper function to safely get nested values
const getNestedValue = (obj: unknown, path: string[]) => {
  return path.reduce(
    (current, key) =>
      current && typeof current === 'object' ? current[key] : undefined,
    obj
  );
};
